'use client';

import { LoadingSpinner } from '@/components/ui/loading-spinner';
import React, { useEffect, useState } from 'react';

interface RentFormProps {
  dictionary: any;
  onSubmit: (contactInfo: { name: string; phone: string; address: string }) => Promise<void>;
  disableSubmit?: boolean;
}

export default function RentForm({ dictionary, onSubmit, disableSubmit = false }: RentFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+32');
  const [address, setAddress] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string; address?: string }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Persist success for the lifetime of the tab/iframe session
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const persisted = window.sessionStorage.getItem('rentFormSubmitted');
        if (persisted === '1') {
          setIsSubmitted(true);
        }
      }
    } catch (_) {
      // best-effort only
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: { name?: string; phone?: string; address?: string } = {};

    if (!name) {
      newErrors.name = dictionary?.sellingForm?.requiredNameField || 'Name is required';
    }
    if (!phone) {
      newErrors.phone = dictionary?.sellingForm?.requiredField || 'This field is required';
    } else if (!/^\+?[0-9\s\-\/()]{6,}$/.test(phone)) {
      newErrors.phone = dictionary?.sellingForm?.invalidPhoneFormat || 'Invalid phone format';
    }
    if (!address) {
      newErrors.address = dictionary?.sellingForm?.requiredField || 'This field is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    await onSubmit({ name, phone, address });
    setIsSubmitting(false);
    setIsSubmitted(true);
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('rentFormSubmitted', '1');
      }
    } catch (_) {
      // ignore persistence failures
    }
  };

  if (isSubmitted) {
    return (
      <div className="w-full text-center p-4 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-base md:text-sm text-green-800">
          {dictionary?.sellingForm?.rentFormSuccess || 'Bedankt! Een verhuurexpert neemt spoedig contact met je op.'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <p className="text-base md:text-sm text-zinc-800 mb-4" style={{ whiteSpace: 'pre-line' }}>
        {dictionary?.sellingForm?.rentFormPrompt || 'Bedankt! Laat hieronder je gegevens achter en een verhuurexpert neemt spoedig contact met je op.'}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="w-full sm:w-[75%]">
          <div>
            <input
              type="text"
              name="name"
              placeholder={dictionary?.sellingForm?.contactNameLabel || 'Your Name'}
              className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              disabled={isSubmitting}
            />
          </div>
          {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
        </div>

        <div className="w-full sm:w-[75%]">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
              <span className="text-base md:text-sm">🇧🇪</span>
              <span className="text-base md:text-sm text-zinc-500">+32</span>
            </div>
            <input
              type="tel"
              inputMode="tel"
              name="phone"
              placeholder={dictionary?.sellingForm?.contactPhone || 'Phone Number'}
              className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow pl-[78px] md:pl-16"
              value={phone.startsWith('+32') ? phone.substring(3) : phone}
              onChange={(e) => {
                const enteredValue = e.target.value;
                const newValue = '+32' + enteredValue.replace(/[^0-9]/g, '');
                setPhone(newValue);
              }}
              autoComplete="tel"
              disabled={isSubmitting}
            />
          </div>
          {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
        </div>

        <div className="w-full sm:w-[75%]">
          <div>
            <input
              type="text"
              name="address"
              placeholder={dictionary?.sellingForm?.propertyAddress || 'Address'}
              className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="street-address"
              disabled={isSubmitting}
            />
          </div>
          {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
        </div>

        <div className="pt-1">
          <button
            type="submit"
            className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center"
            disabled={isSubmitting || disableSubmit}
          >
            {isSubmitting ? (
              <LoadingSpinner />
            ) : (
              dictionary?.sellingForm?.submitButton || 'Submit'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
